import { useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  PageHeader, PrimaryButton, Modal, Field, TextInput, Select, Textarea,
  PriorityBadge, StatusPill, useFetch, Pagination,
} from "../../components/crm/ui";
import { Plus, Trash2, Search, Filter, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import { formatApiError } from "../../lib/api";

const TYPES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "housing_society", label: "Housing Society" },
];
const PRIORITIES = ["premium", "moderate", "low"];

const SOURCE_LABEL = {
  crm: "Direct CRM",
  public_quote: "Quote Calculator",
  campaign: "Campaign",
  public_survey: "Campaign",
  whatsapp_chat: "WhatsApp",
  sc_referral: "Solar Consultant",
  public_signup: "Self-signup",
  public_app: "Customer App",
};
const SOURCE_FILTERS = [{ value: "all", label: "All sources" }, ...Object.entries(
  { crm: "Direct CRM", public_quote: "Quote Calculator", campaign: "Campaign", whatsapp_chat: "WhatsApp", sc_referral: "Solar Consultant", public_signup: "Self-signup", public_app: "Customer App" }
).map(([value, label]) => ({ value, label }))];

const CHART_COLORS = ["#1F4E2C", "#F39C12", "#0EA5E9", "#94A3B8", "#DC2626", "#7C3AED", "#0D9488"];

const EMPTY = {
  customer_name: "", customer_type: "residential", priority: "moderate",
  phone: "", email: "", address: "", pin_code: "",
  customer_group_id: "", consumer_name: "", consumer_phone: "", consumer_email: "",
};

function KpiChip({ label, value, tone = "bg-slate-100 text-slate-800" }) {
  return (
    <div className={"px-2.5 py-1.5 flex items-center gap-1.5 " + tone}>
      <div className="font-display text-[16px] font-bold leading-none tabular-nums">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="border border-[#E2E4DC] bg-white p-3">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] mb-2">{title}</div>
      <div style={{ width: "100%", height: 150 }}>{children}</div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">{label}</div>
      {children}
    </div>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const { data, reload } = useFetch(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`, [q]);
  const { data: groups } = useFetch("/customer-groups");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [filterOpen, setFilterOpen] = useState(false);
  const [fType, setFType] = useState("");
  const [fGroup, setFGroup] = useState("");
  const [fName, setFName] = useState("");
  const [fEmail, setFEmail] = useState("");
  const [fSource, setFSource] = useState("all");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const activeFilters = useMemo(
    () => [fType, fGroup, fName, fEmail, fSource !== "all" ? fSource : ""].filter(Boolean).length,
    [fType, fGroup, fName, fEmail, fSource]
  );

  function clearFilters() {
    setFType(""); setFGroup(""); setFName(""); setFEmail(""); setFSource("all"); setQ(""); setPage(1);
  }

  const filtered = useMemo(() => {
    return (data || []).filter((c) => {
      if (fType && c.customer_type !== fType) return false;
      if (fGroup && c.customer_group_id !== fGroup) return false;
      if (fName && !(c.customer_name || "").toLowerCase().includes(fName.trim().toLowerCase())) return false;
      if (fEmail && !(c.email || "").toLowerCase().includes(fEmail.trim().toLowerCase())) return false;
      if (fSource !== "all" && c.source !== fSource) return false;
      return true;
    });
  }, [data, fType, fGroup, fName, fEmail, fSource]);

  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  // ---- Executive analytics (computed client-side from the full filtered set) ----
  const analytics = useMemo(() => {
    const byType = {}; const bySource = {}; const byMonth = {};
    for (const c of filtered) {
      byType[c.customer_type] = (byType[c.customer_type] || 0) + 1;
      const src = SOURCE_LABEL[c.source] || c.source || "Direct CRM";
      bySource[src] = (bySource[src] || 0) + 1;
      const month = (c.created_at || "").slice(0, 7);
      if (month) byMonth[month] = (byMonth[month] || 0) + 1;
    }
    const typeData = TYPES.map((t) => ({ name: t.label, value: byType[t.value] || 0 })).filter((d) => d.value > 0);
    const sourceData = Object.entries(bySource).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    const months = Object.keys(byMonth).sort().slice(-6);
    const trendData = months.map((m) => ({ month: m.slice(5), count: byMonth[m] }));
    return { typeData, sourceData, trendData, total: filtered.length, indirect: filtered.filter((c) => c.is_indirect_customer).length };
  }, [filtered]);

  function startCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function startEdit(c) {
    setEditing(c);
    setForm({
      customer_name: c.customer_name || "", customer_type: c.customer_type, priority: c.priority,
      phone: c.phone || "", email: c.email || "", address: c.address || "", pin_code: c.pin_code || "",
      customer_group_id: c.customer_group_id || "",
      consumer_name: c.consumer_name || "", consumer_phone: c.consumer_phone || "",
      consumer_email: c.consumer_email || "",
    });
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    if (!payload.customer_group_id) delete payload.customer_group_id;
    if (payload.customer_type === "residential") {
      payload.consumer_name = payload.customer_name;
      payload.consumer_phone = payload.phone;
      payload.consumer_email = payload.email;
    }
    try {
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(c) {
    if (!window.confirm(`Delete ${c.customer_name}?`)) return;
    await api.delete(`/customers/${c.id}`); toast.success("Deleted"); reload();
  }

  const isResidential = form.customer_type === "residential";

  return (
    <div data-testid="customers-page">
      <PageHeader
        overline="Master Data · Executive Report"
        title="Customers"
        action={
          <PrimaryButton onClick={startCreate} testid="create-customer-btn">
            <Plus className="w-4 h-4" /> New Customer
          </PrimaryButton>
        }
      />

      {/* KPI strip */}
      <div className="flex items-center gap-1 mb-3 flex-wrap">
        <KpiChip label="Total Customers" value={analytics.total} tone="bg-[#1F4E2C] text-white" />
        <KpiChip label="Residential" value={analytics.typeData.find((d) => d.name === "Residential")?.value || 0} />
        <KpiChip label="Commercial" value={analytics.typeData.find((d) => d.name === "Commercial")?.value || 0} />
        <KpiChip label="Housing Society" value={analytics.typeData.find((d) => d.name === "Housing Society")?.value || 0} />
        <KpiChip label="Indirect (App Self-reg)" value={analytics.indirect} tone="bg-amber-50 text-amber-800" />
      </div>

      {/* Executive charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="customers-analytics">
        <ChartCard title="Customers by Source">
          <ResponsiveContainer>
            <BarChart data={analytics.sourceData} layout="vertical" margin={{ left: 4, right: 8, top: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 9, fontFamily: "monospace" }} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
              <Bar dataKey="value" fill="#1F4E2C" barSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Customer Type Mix">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={analytics.typeData} dataKey="value" nameKey="name" innerRadius={35} outerRadius={55} paddingAngle={2}>
                {analytics.typeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="New Customers (last 6 months)">
          <ResponsiveContainer>
            <AreaChart data={analytics.trendData} margin={{ left: -20, right: 8, top: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fontFamily: "monospace" }} />
              <YAxis tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
              <Area type="monotone" dataKey="count" stroke="#F39C12" fill="#FEF3C7" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Search + filter row */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 flex items-center gap-2 border border-[#DDE5D8] bg-white px-2 py-1.5 max-w-md">
          <Search className="w-3.5 h-3.5 text-[#94A3B8]" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by name, phone, email…"
            data-testid="search-input"
            className="flex-1 text-[12.5px] font-mono outline-none bg-transparent"
          />
          {q && <button onClick={() => setQ("")}><X className="w-3.5 h-3.5 text-[#94A3B8] hover:text-[#0F1A12]" /></button>}
        </div>
        <button
          onClick={() => setFilterOpen((v) => !v)}
          data-testid="customers-filter-toggle"
          className={"px-2.5 py-1.5 border font-mono text-[10.5px] uppercase tracking-widest inline-flex items-center gap-1.5 " + (filterOpen ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "bg-white text-[#0F1A12] border-[#DDE5D8] hover:border-[#1F4E2C]")}
        >
          <Filter className="w-3.5 h-3.5" /> Filters{activeFilters > 0 && ` · ${activeFilters}`}
          {filterOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {activeFilters > 0 && (
          <button onClick={clearFilters} data-testid="customers-filter-clear" className="text-[10.5px] font-mono uppercase tracking-widest text-[#475569] hover:text-[#0F1A12] px-2">Reset</button>
        )}
        <div className="font-mono text-[10.5px] text-[#475569]">
          <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {(data || []).length}
        </div>
      </div>

      {filterOpen && (
        <div className="mb-3 border border-[#DDE5D8] bg-[#FFFDF8] p-3 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="customers-filter-panel">
          <FilterField label="Customer Type">
            <Select value={fType} onChange={(e) => { setFType(e.target.value); setPage(1); }} data-testid="filter-customer-type">
              <option value="">— All —</option>
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Customer Group">
            <Select value={fGroup} onChange={(e) => { setFGroup(e.target.value); setPage(1); }} data-testid="filter-customer-group">
              <option value="">— All —</option>
              {(groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Name">
            <input value={fName} onChange={(e) => { setFName(e.target.value); setPage(1); }} placeholder="Contains…" data-testid="filter-name"
              className="w-full border border-[#DDE5D8] px-2 py-1.5 text-[12px] font-mono" />
          </FilterField>
          <FilterField label="Email ID">
            <input value={fEmail} onChange={(e) => { setFEmail(e.target.value); setPage(1); }} placeholder="Contains…" data-testid="filter-email"
              className="w-full border border-[#DDE5D8] px-2 py-1.5 text-[12px] font-mono" />
          </FilterField>
          <FilterField label="Source">
            <Select value={fSource} onChange={(e) => { setFSource(e.target.value); setPage(1); }} data-testid="filter-source">
              {SOURCE_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </FilterField>
        </div>
      )}

      <div className="border border-[#E2E4DC] bg-white overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="customers-table">
          <thead>
            <tr className="text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] border-b border-[#E2E4DC] bg-[#F4F6F1]">
              <th className="px-3 py-2.5">Name</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Priority</th>
              <th className="px-3 py-2.5">Group</th>
              <th className="px-3 py-2.5">Phone</th>
              <th className="px-3 py-2.5">Email</th>
              <th className="px-3 py-2.5">Consumer</th>
              <th className="px-3 py-2.5">Source</th>
              <th className="px-3 py-2.5">Created</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((c) => (
              <tr key={c.id} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer" data-testid={`customer-row-${c.id}`} onClick={() => startEdit(c)}>
                <td className="px-3 py-2 font-medium">
                  {c.customer_name}
                  {c.is_indirect_customer && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800" data-testid={`indirect-badge-${c.id}`}>
                      Indirect
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-widest">{c.customer_type.replace("_", " ")}</td>
                <td className="px-3 py-2"><PriorityBadge value={c.priority} /></td>
                <td className="px-3 py-2 text-[11px] text-[#4A5568]">{c.customer_group_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{c.phone}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{c.email}</td>
                <td className="px-3 py-2 text-[11px] text-[#4A5568]">{c.consumer_name}</td>
                <td className="px-3 py-2"><StatusPill value={SOURCE_LABEL[c.source] || c.source} tone={c.source === "crm" ? "default" : "info"} /></td>
                <td className="px-3 py-2 font-mono text-[10.5px] text-[#475569] whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => remove(c)} className="p-1.5 hover:text-red-600" data-testid={`delete-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-[#4A5568]">
                {(data || []).length === 0 ? "No customers yet." : "No records match the filters."}
              </td></tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} pageSize={pageSize} total={filtered.length}
          onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} testid="customers-pagination" />
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Customer" : "New Customer"} wide>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customer-form">
          <Field label="Customer Name *">
            <TextInput required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="cust-name" />
          </Field>
          <Field label="Type *">
            <Select value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} data-testid="cust-type">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="cust-priority">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="Customer Group">
            <Select value={form.customer_group_id} onChange={(e) => setForm({ ...form, customer_group_id: e.target.value })}>
              <option value="">— None —</option>
              {(groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
          </Field>
          <Field label="Phone *">
            <TextInput required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Email *">
            <TextInput type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Address" className="md:col-span-2">
            <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <Field label="PIN Code">
            <TextInput maxLength={6} value={form.pin_code} onChange={(e) => setForm({ ...form, pin_code: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="560001" data-testid="cust-pin" />
          </Field>

          <div className="md:col-span-2 border-t border-[#E2E4DC] pt-4 mt-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#4A5568] mb-3">
              Consumer (Decision Maker) {isResidential && "— same as customer for residential"}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Consumer Name">
                <TextInput disabled={isResidential} value={isResidential ? form.customer_name : form.consumer_name}
                  onChange={(e) => setForm({ ...form, consumer_name: e.target.value })} data-testid="consumer-name" />
              </Field>
              <Field label="Consumer Phone">
                <TextInput disabled={isResidential} value={isResidential ? form.phone : form.consumer_phone}
                  onChange={(e) => setForm({ ...form, consumer_phone: e.target.value })} />
              </Field>
              <Field label="Consumer Email">
                <TextInput type="email" disabled={isResidential} value={isResidential ? form.email : form.consumer_email}
                  onChange={(e) => setForm({ ...form, consumer_email: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">
              Cancel
            </button>
            <PrimaryButton type="submit" disabled={saving} testid="cust-save-btn">
              {saving ? "Saving…" : editing ? "Save changes" : "Create"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
